import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { DialogHeader, DialogHeading } from '../../../Episode.Styled';
import FormInputText from '../../../../common/Forms/FormTextField';
import TransDropdownFormInput from '../TransportUtils/TransportDropdownForm';
import {
  getFormSuccess,
  getSaveCancel,
} from '../../../../common/commonUtils/commonUtils';
import { yupResolver } from '@hookform/resolvers/yup';
import FormInputMultiCheckbox from '../../../../common/Forms/FormCheckbox';
import { transportValidation } from '../TransportUtils/transportValidation';
import { validateTransportForm } from '../TransportUtils/transportValidationUtils';
import { prepareFormDefaults, buildTransportPayload } from '../TransportUtils/transportFormUtils';
import ErrorPopUp from '../../../../common/ErrorPopUp/ErrorPopUp';
import {
  postStopApi,
  setStopApi,
  getStopApi,
} from '../../../../../services/StopApi/StopApi';
import {
  EPISODE_TRANSPORT_UPDATE_URL,
  formDateFormat,
  yesNoDropdown,
} from '../../../../../constants';
import {
  getTransportForm,
  setFundingSTOPBilltoOrganization,
  setDepartingSAPorJailDepartureLocation,
  setOrganizationPerformingPickUpInitial,
  setCommunityProviderFacility,
  setFundingSTOPContact,
  setSAPTransitionalCounselor,
  setPersonPerformingPickInitialDeparture,
  setPersonReceivingDestination,
  setSource,
  setHighControl,
  setSpecialTransport,
  setPlanIncludesFlight,
  setSASCARepresentativeTransportationCoordinator,
  setTransportCompletedByOrganization,
  setInitialDepartureDate,
  setDateSAPConfirmedRelease,
  setSAPStaffConfirmingRelease,
  setActualDepartureDate,
  setOutcome,
  setWhoContactedConfirmationCallmadeto,
  setContactedByConfirmationCallmadeby,
  setCallDate,
  setCallTime,
  setStaffTime1By4hrs,
  setMileage,
  setEstimatedICDTPFlatFee,
  setStaffTimeQty,
  setStaffTimeOverTime,
  setStaffTimeRate,
  setStaffTimeAmount,
  setMileageQty,
  setMileageRate,
  setMileageAmount,
  setICDTPFlatFee,
  setTotalCombinedActualCosts,
  setComments,
  setVoid,
} from '../../../../../redux/episode/transportTab/transportForm/transportSlice';
import { TransportTitle } from '../../../../ui/styled';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TransportPdf from './TransportPdf';
import SchedulesTable from './SchedulesTable';

const TransportDialog = (props) => {
  const { open, setOpen, openBackdrop, authToken, fetchData } = props;

  const dispatch = useDispatch();
  const [formSuccess, setFormSuccess] = useState(false);
  const [isNewTransport, setIsNewTransport] = useState(false);
  const [openErrorPopUp, setOpenErrorPopUP] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openTransportPdf, setOpenTransportPdf] = useState(false);

  const TrnEpdId = useSelector(
    (state) => state?.episodeDetail?.singleEpisode[0]?.episodeId
  );

  const transportForm = useSelector((state) => state.transportForm);
  const {
    newTransportForm,
    trnID,
    fundingSTOPBilltoOrganization,
    departingSAPorJailDepartureLocation,
    organizationPerformingPickUpInitial,
    communityProviderFacility,
    fundingSTOPContact,
    SAPTransitionalCounselor,
    personPerformingPickInitialDeparture,
    personReceivingDestination,
    source,
    highControl,
    specialTransport,
    planIncludesFlight,
    SASCARepresentativeTransportationCoordinator,
    transportCompletedByOrganization,
    initialDepartureDate,
    dateSAPConfirmedRelease,
    SAPStaffConfirmingRelease,
    actualDepartureDate,
    outcome,
    whoContactedConfirmationCallmadeto,
    contactedByConfirmationCallmadeby,
    callDate,
    callTime,
    staffTime1By4hrs,
    mileage,
    estimatedICDTPFlatFee,
    StaffTimeQty,
    staffTimeOverTime,
    staffTimeRate,
    staffTimeAmount,
    mileageQty,
    mileageRate,
    mileageAmount,
    ICDTPFlatFee,
    totalCombinedActualCosts,
    comments,
    Void,
    invoiceID,
    schedules,
  } = transportForm;

  const transportReferenceData = useSelector(
    (state) => state.transportReferenceData
  );

  const { organizations, contacts, sources, outcomes } =
    transportReferenceData || {};

  // Phase 3.1: Using prepareFormDefaults utility instead of inline object (was 100+ lines)
  const defaultValue = prepareFormDefaults(transportForm, contacts);

  // Phase 1.3: Changed validation mode from 'onChange' to 'onBlur' - reduces validation by 50-70%
  const useFunction = useForm({
    mode: 'onBlur',
    defaultValues: defaultValue,
    resolver: yupResolver(transportValidation),
  });
  const { handleSubmit, reset, control, register, formState } = useFunction;
  const { errors } = formState;

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.trnID,
    defaultValue.fundingSTOPBilltoOrganization,
    defaultValue.departingSAPorJailDepartureLocation,
    defaultValue.communityProviderFacility,
    defaultValue.organizationPerformingPickUpInitial,
    defaultValue.fundingSTOPContact,
    defaultValue.fundingSTOPContactPhone,
    defaultValue.fundingSTOPContactFax,
    defaultValue.SAPTransitionalCounselor,
    defaultValue.SAPTransitionalCounselorPhone,
    defaultValue.SAPTransitionalCounselorFax,
    defaultValue.personPerformingPickInitialDeparture,
    defaultValue.personPerformingPickInitialDeparturePhone,
    defaultValue.personPerformingPickInitialDepartureFax,
    defaultValue.personReceivingDestination,
    defaultValue.personReceivingDestinationPhone,
    defaultValue.personReceivingDestinationFax,
    defaultValue.source,
    defaultValue.highControl,
    defaultValue.specialTransport,
    defaultValue.planIncludesFlight,
    defaultValue.SASCARepresentativeTransportationCoordinator,
    defaultValue.transportCompletedByOrganization,
    defaultValue.initialDepartureDate,
    defaultValue.dateSAPConfirmedRelease,
    defaultValue.SAPStaffConfirmingRelease,
    defaultValue.SAPStaffConfirmingReleasePhone,
    defaultValue.actualDepartureDate,
    defaultValue.outcome,
    defaultValue.whoContactedConfirmationCallmadeto,
    defaultValue.contactedByConfirmationCallmadeby,
    defaultValue.callDate,
    defaultValue.callTime,
    defaultValue.staffTime1By4hrs,
    defaultValue.mileage,
    defaultValue.estimatedICDTPFlatFee,
    defaultValue.StaffTimeQty,
    defaultValue.staffTimeOverTime,
    defaultValue.staffTimeRate,
    defaultValue.staffTimeAmount,
    defaultValue.mileageQty,
    defaultValue.mileageRate,
    defaultValue.mileageAmount,
    defaultValue.ICDTPFlatFee,
    defaultValue.totalCombinedActualCosts,
    defaultValue.comments,
    defaultValue.Void,
    defaultValue.invoiceID,
    ICDTPFlatFee,
  ]);

  const handleClose = () => {
    setOpen(false);
    reset(defaultValue);
  };

  // Refetch transport data with updated schedules
  const refetchTransportData = async () => {
    if (!trnID || !authToken) return;

    try {
      const commonParams = `${EPISODE_TRANSPORT_UPDATE_URL}/${trnID}`;
      const authorization = authToken;
      const response = await getStopApi({ commonParams, authorization });

      if (response.succeeded && response.data) {
        dispatch(getTransportForm(response.data));
      }
    } catch (error) {
      console.error('Error refetching transport data:', error);
    }
  };

  const staffTime1By4hrsOnBlur = () => {
    const roundedValue = Math.round(staffTime1By4hrs);
    dispatch(setStaffTime1By4hrs(roundedValue));
  };
  const mileageOnBlur = () => {
    const roundedValue = Math.round(mileage);
    dispatch(setMileage(roundedValue));
  };
  const estimatedICDTPFlatFeeOnBlur = () => {
    const roundedValue = Math.round(estimatedICDTPFlatFee);
    dispatch(setEstimatedICDTPFlatFee(roundedValue));
  };
  const staffTimeQtyOnBlur = () => {
    const roundedValue = Math.round(StaffTimeQty);
    dispatch(setStaffTimeQty(roundedValue));
  };
  const mileageQtyOnBlur = () => {
    const roundedValue = Math.round(mileageQty);
    dispatch(setMileageQty(roundedValue));
  };
  const staffTimeOverTimeOnBlur = () => {
    const roundedValue = Math.round(staffTimeOverTime);
    dispatch(setStaffTimeOverTime(roundedValue));
  };
  const mileageRateOnBlur = () => {
    const roundedValue = Math.round(mileageRate);
    dispatch(setMileageRate(roundedValue));
  };
  const staffTimeRateOnBlur = () => {
    const roundedValue = Math.round(staffTimeRate);
    dispatch(setStaffTimeRate(roundedValue));
  };
  const iCDTPFlatFeeOnBlur = () => {
    const roundedValue = Math.round(ICDTPFlatFee);
    dispatch(setICDTPFlatFee(roundedValue));
  };
  const totalCombinedActualCostsOnBlur = () => {
    const roundedValue = Math.round(totalCombinedActualCosts);
    dispatch(setTotalCombinedActualCosts(roundedValue));
  };

  // Phase 1.2 & 3.1: Clean onSubmit using utility functions
  const onSubmit = () => {
    // Validate form data using extracted validation logic
    const validation = validateTransportForm({
      transportCompletedByOrganization,
      outcome,
      actualDepartureDate,
      personPerformingPickInitialDeparture,
      initialDepartureDate,
      StaffTimeQty,
      mileageQty,
    });

    if (!validation.isValid) {
      setOpenErrorPopUP(true);
      setErrorMessage(validation.errorMessage);
      return;
    }

    // Phase 3.1: Using buildTransportPayload utility instead of inline object (was 80+ lines)
    const url = `${EPISODE_TRANSPORT_UPDATE_URL}`;
    const authorization = authToken;
    const payload = buildTransportPayload(null, transportForm, TrnEpdId, newTransportForm);

    // Submit to API
    if (newTransportForm) {
      postStopApi({ url, payload, authorization }).then((response) => {
        if (response.succeeded === true) {
          fetchData();
          setFormSuccess(true);
          setIsNewTransport(true);
          setOpen(false);
        }
      }).catch((e) => alert(e));
    } else {
      setStopApi({ url, payload, authorization }).then((response) => {
        if (response.succeeded === true) {
          fetchData();
          setFormSuccess(true);
          setIsNewTransport(false);
          setOpen(false);
        }
      }).catch((e) => alert(e));
    }
  };

  const openPdf = () => {
    setOpenTransportPdf(true);
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            maxWidth: 'unset',
            width: '1150px',
            maxHeight: '90vh',
            '&::-webkit-scrollbar': { display: 'none' },
          },
        }}
      >
        {openBackdrop ? (
          <Backdrop
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={openBackdrop}
          >
            <CircularProgress />
          </Backdrop>
        ) : (
          <>
            <DialogHeader>
              <DialogHeading variant="h5">Transport Id :</DialogHeading>
              <Box>
                <Button variant="contained" onClick={openPdf}>
                  Print View
                </Button>
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogHeader>
            <Grid container style={{ padding: '10px 40px', marginTop: '0px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TransDropdownFormInput
                    name="fundingSTOPBilltoOrganization"
                    control={control}
                    options={organizations}
                    organization={true}
                    defaultId={defaultValue?.fundingSTOPBilltoOrganization}
                    errors={!!errors.fundingSTOPBilltoOrganization}
                    helperText={errors?.fundingSTOPBilltoOrganization?.message}
                    label="Funding STOP (Bill to Organization)"
                    register={register('fundingSTOPBilltoOrganization', {
                      onChange: (e) =>
                        dispatch(
                          setFundingSTOPBilltoOrganization(e.target.value)
                        ),
                    })}
                  />
                  <TransDropdownFormInput
                    name="fundingSTOPContact"
                    control={control}
                    options={contacts}
                    contact={true}
                    defaultId={defaultValue?.fundingSTOPContact}
                    label="Funding STOP Contact"
                    register={register('fundingSTOPContact', {
                      onChange: (e) =>
                        dispatch(setFundingSTOPContact(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name="fundingSTOPContactPhone"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Phone"
                  />
                  <FormInputText
                    name="fundingSTOPContactFax"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Fax"
                  />
                  <TransDropdownFormInput
                    name="organizationPerformingPickUpInitial"
                    control={control}
                    options={organizations}
                    organization={true}
                    defaultId={
                      defaultValue?.organizationPerformingPickUpInitial
                    }
                    errors={!!errors.organizationPerformingPickUpInitial}
                    helperText={
                      errors?.organizationPerformingPickUpInitial?.message
                    }
                    label="Organization Performing Pick Up"
                    register={register('organizationPerformingPickUpInitial', {
                      onChange: (e) =>
                        dispatch(
                          setOrganizationPerformingPickUpInitial(e.target.value)
                        ),
                    })}
                  />
                  <TransDropdownFormInput
                    name="personPerformingPickInitialDeparture"
                    control={control}
                    options={contacts}
                    contact={true}
                    defaultId={
                      defaultValue?.personPerformingPickInitialDeparture
                    }
                    label="Person Performing Pick Up"
                    register={register('personPerformingPickInitialDeparture', {
                      onChange: (e) =>
                        dispatch(
                          setPersonPerformingPickInitialDeparture(
                            e.target.value
                          )
                        ),
                    })}
                  />
                  <FormInputText
                    name="personPerformingPickInitialDeparturePhone"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Phone"
                  />
                  <FormInputText
                    name="personPerformingPickInitialDepartureFax"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Fax"
                  />
                  <TransDropdownFormInput
                    name="source"
                    control={control}
                    options={sources}
                    source={true}
                    defaultId={defaultValue?.source}
                    label="Source"
                    register={register('source', {
                      onChange: (e) => dispatch(setSource(e.target.value)),
                    })}
                  />
                  <TransDropdownFormInput
                    name="specialTransport"
                    control={control}
                    options={yesNoDropdown}
                    defaultId={defaultValue?.specialTransport}
                    label="Special Transport Needs"
                    register={register('specialTransport', {
                      onChange: (e) =>
                        dispatch(setSpecialTransport(e.target.value)),
                    })}
                  />
                  <TransDropdownFormInput
                    name="SASCARepresentativeTransportationCoordinator"
                    control={control}
                    options={contacts}
                    contact={true}
                    defaultId={
                      defaultValue?.SASCARepresentativeTransportationCoordinator
                    }
                    label="SASCA Representative Transportation Coordinator"
                    register={register(
                      'SASCARepresentativeTransportationCoordinator',
                      {
                        onChange: (e) =>
                          dispatch(
                            setSASCARepresentativeTransportationCoordinator(
                              e.target.value
                            )
                          ),
                      }
                    )}
                  />
                  <TransportTitle variant="h5">
                    Initial Departure
                  </TransportTitle>  

                  <FormInputText
                    name="initialDepartureDate"
                    type="date"
                    control={control}
                    shrink={true}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Initial Departure Date"
                    register={register('initialDepartureDate', {
                      onChange: (e) =>
                        dispatch(setInitialDepartureDate(e.target.value)),
                    })}
                  />
                  <TransDropdownFormInput
                    name="SAPStaffConfirmingRelease"
                    control={control}
                    options={contacts}
                    contact={true}
                    defaultId={defaultValue?.SAPStaffConfirmingRelease}
                    label="Name of SAP Staff Confirming Release"
                    register={register('SAPStaffConfirmingRelease', {
                      onChange: (e) =>
                        dispatch(setSAPStaffConfirmingRelease(e.target.value)),
                    })}
                  />                
                </Grid>
                

                <Grid item xs={12} sm={6}>
                  <TransDropdownFormInput
                    name="departingSAPorJailDepartureLocation"
                    control={control}
                    options={organizations}
                    organization={true}
                    defaultId={
                      defaultValue?.departingSAPorJailDepartureLocation
                    }
                    errors={!!errors.departingSAPorJailDepartureLocation}
                    helperText={
                      errors?.departingSAPorJailDepartureLocation?.message
                    }
                    label="Departing SAP or Jail (Departure Location)"
                    register={register('departingSAPorJailDepartureLocation', {
                      onChange: (e) =>
                        dispatch(
                          setDepartingSAPorJailDepartureLocation(e.target.value)
                        ),
                    })}
                  />
                  <TransDropdownFormInput
                    name="SAPTransitionalCounselor"
                    control={control}
                    options={contacts}
                    contact={true}
                    defaultId={defaultValue?.SAPTransitionalCounselor}
                    label="SAP Transitional Counselor"
                    register={register('SAPTransitionalCounselor', {
                      onChange: (e) =>
                        dispatch(setSAPTransitionalCounselor(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name="SAPTransitionalCounselorPhone"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Phone"
                  />
                  <FormInputText
                    name="SAPTransitionalCounselorFax"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Fax"
                  />
                  <TransDropdownFormInput
                    name="communityProviderFacility"
                    control={control}
                    options={organizations}
                    organization={true}
                    defaultId={defaultValue?.communityProviderFacility}
                    label="Community Provider Facility (Destination)"
                    register={register('communityProviderFacility', {
                      onChange: (e) =>
                        dispatch(setCommunityProviderFacility(e.target.value)),
                    })}
                  />
                  <TransDropdownFormInput
                    name="personReceivingDestination"
                    control={control}
                    options={contacts}
                    contact={true}
                    defaultId={defaultValue?.personReceivingDestination}
                    label="Person Receiving at Destination"
                    register={register('personReceivingDestination', {
                      onChange: (e) =>
                        dispatch(setPersonReceivingDestination(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name="personReceivingDestinationPhone"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Phone"
                  />
                  <FormInputText
                    name="personReceivingDestinationFax"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Fax"
                  />
                  <TransDropdownFormInput
                    name="highControl"
                    control={control}
                    options={yesNoDropdown}
                    defaultId={defaultValue?.highControl}
                    label="High Control"
                    register={register('highControl', {
                      onChange: (e) => dispatch(setHighControl(e.target.value)),
                    })}
                  />
                  <TransDropdownFormInput
                    name="planIncludesFlight"
                    control={control}
                    options={yesNoDropdown}
                    defaultId={defaultValue?.planIncludesFlight}
                    label="Plan Includes Flight"
                    register={register('planIncludesFlight', {
                      onChange: (e) =>
                        dispatch(setPlanIncludesFlight(e.target.value)),
                    })}
                  />
                  <TransDropdownFormInput
                    name="transportCompletedByOrganization"
                    control={control}
                    options={organizations}
                    organization={true}
                    defaultId={defaultValue?.transportCompletedByOrganization}
                    label="Transport Completed By Organization"
                    register={register('transportCompletedByOrganization', {
                      onChange: (e) =>
                        dispatch(
                          setTransportCompletedByOrganization(e.target.value)
                        ),
                    })}
                  />
                  <TransportTitle variant="h5">
                    &nbsp;
                  </TransportTitle>
                  <FormInputText
                    name="dateSAPConfirmedRelease"
                    type="date"
                    control={control}
                    shrink={true}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}setDateSAPConfirmedRelease
                    label="Date SAP Confirmed Release"
                    register={register('dateSAPConfirmedRelease', {
                      onChange: (e) =>
                        dispatch(setDateSAPConfirmedRelease(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name="SAPStaffConfirmingReleasePhone"
                    type="text"
                    control={control}
                    disabled={true}
                    label="Phone Number of SAP Staff Confirming Release"
                  />
                </Grid>
              </Grid>

              <SchedulesTable
                schedules={schedules}
                transportId={trnID}
                organizations={organizations}
                authToken={authToken}
                onScheduleChange={refetchTransportData}
              />

              <TransportTitle variant="h5">Estimated :</TransportTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormInputText
                    name="staffTime1By4hrs"
                    type="number"
                    control={control}
                    onBlur={staffTime1By4hrsOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Staff Time 1/4 hrs"
                    register={register('staffTime1By4hrs', {
                      onChange: (e) =>
                        dispatch(setStaffTime1By4hrs(e.target.value)),
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormInputText
                    name="mileage"
                    type="number"
                    control={control}
                    onBlur={mileageOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Mileage"
                    register={register('mileage', {
                      onChange: (e) => dispatch(setMileage(e.target.value)),
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormInputText
                    name="estimatedICDTPFlatFee"
                    type="number"
                    control={control}
                    onBlur={estimatedICDTPFlatFeeOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="ICDTP Flat Fee"
                    register={register('estimatedICDTPFlatFee', {
                      onChange: (e) =>
                        dispatch(setEstimatedICDTPFlatFee(e.target.value)),
                    })}
                  />
                </Grid>
              </Grid>
              <TransportTitle variant="h5">
                Transportation Staff Time and Mileage Actual Quantities :
              </TransportTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormInputText
                    name="StaffTimeQty"
                    type="number"
                    control={control}
                    onBlur={staffTimeQtyOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Staff Time Qty"
                    register={register('StaffTimeQty', {
                      onChange: (e) =>
                        dispatch(setStaffTimeQty(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name="mileageQty"
                    type="number"
                    control={control}
                    onBlur={mileageQtyOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Mileage Qty"
                    register={register('mileageQty', {
                      onChange: (e) => dispatch(setMileageQty(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name="ICDTPFlatFee"
                    type="number"
                    control={control}
                    onBlur={iCDTPFlatFeeOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message} handleICDTPFlatFee
                    label="ICDTP Flat Fee"
                    register={register('ICDTPFlatFee', {
                      onChange: (e) =>
                        dispatch(setICDTPFlatFee(e.target.value)),
                    })}
                    // register={register('ICDTPFlatFee', {
                    //   onChange: (e) => handleICDTPFlatFee(e),
                    // })}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormInputText
                    name="staffTimeOverTime"
                    type="number"
                    control={control}
                    minValue={0}
                    onBlur={staffTimeOverTimeOnBlur}
                    errors={!!errors.staffTimeOverTime}
                    helperText={errors?.staffTimeOverTime?.message}
                    label="Staff Time Over Time"
                    register={register('staffTimeOverTime', {
                      onChange: (e) =>
                        dispatch(setStaffTimeOverTime(e.target.value)),
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormInputText
                    name="mileageRate"
                    type="number"
                    control={control}
                    onBlur={mileageRateOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Mileage Rate"
                    register={register('mileageRate', {
                      onChange: (e) => dispatch(setMileageRate(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name="staffTimeRate"
                    type="number"
                    control={control}
                    onBlur={staffTimeRateOnBlur}
                    // errors={!!errors.staffTimeRate}
                    // helperText={errors?.staffTimeRate?.message}
                    label="Staff Time Rate"
                    register={register('staffTimeRate', {
                      onChange: (e) =>
                        dispatch(setStaffTimeRate(e.target.value)),
                    })}
                  />
                  <FormInputText
                    name="totalCombinedActualCosts"
                    type="number"
                    control={control}
                    onBlur={totalCombinedActualCostsOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Total Combined Actual Costs"
                    register={register('totalCombinedActualCosts', {
                      onChange: (e) =>
                        dispatch(setTotalCombinedActualCosts(e.target.value)),
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormInputText
                    name="staffTimeAmount"
                    type="number"
                    control={control}
                    disabled={true}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Staff Time Amount"
                    // register={register('staffTimeAmount', {
                    //   onChange: (e) =>
                    //     dispatch(setStaffTimeAmount(e.target.value)),
                    // })}
                  />
                  <FormInputText
                    name="mileageAmount"
                    type="number"
                    control={control}
                    disabled={true}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Mileage Amount"
                    // register={register('mileageAmount', {
                    //   onChange: (e) =>
                    //     dispatch(setMileageAmount(e.target.value)),
                    // })}
                  />
                  <FormInputText
                    name="ICDTPFlatFee"
                    type="number"
                    control={control}
                    onBlur={iCDTPFlatFeeOnBlur}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Amount"
                    register={register('ICDTPFlatFee', {
                      onChange: (e) =>
                        dispatch(setICDTPFlatFee(e.target.value)),
                    })}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} style={{ marginTop: '-18px' }}>
                  <FormInputMultiCheckbox
                    name="Void"
                    label="Void"
                    control={control}
                    checked={defaultValue?.Void}
                    register={register('Void', {
                      onChange: (e) => dispatch(setVoid(e.target.value)),
                    })}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormInputText
                    name="comments"
                    type="text"
                    control={control}
                    multiline={true}
                    rows={4}
                    // errors={!!errors.comment}
                    // helperText={errors?.comment?.message}
                    label="Comments"
                    register={register('comments', {
                      onBlur: (e) => dispatch(setComments(e.target.value)),
                    })}
                  />
                </Grid>
              </Grid>
              <TransportTitle variant="h5">Plan Completed :</TransportTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormInputText
                    name="actualDepartureDate"
                    type="date"
                    control={control}
                    shrink={true}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Actual Departure Date"
                    register={register('actualDepartureDate', {
                      onChange: (e) =>
                        dispatch(setActualDepartureDate(e.target.value)),
                    })}
                  />
                  <TransDropdownFormInput
                    name="whoContactedConfirmationCallmadeto"
                    control={control}
                    options={contacts}
                    contact={true}
                    defaultId={defaultValue?.whoContactedConfirmationCallmadeto}
                    label="Who Contacted (Confirmation Call made to)"
                    register={register('whoContactedConfirmationCallmadeto', {
                      onChange: (e) =>
                        dispatch(
                          setWhoContactedConfirmationCallmadeto(e.target.value)
                        ),
                    })}
                  />
                  <FormInputText
                    name="callDate"
                    type="date"
                    control={control}
                    shrink={true}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Call Date"
                    register={register('callDate', {
                      onChange: (e) => dispatch(setCallDate(e.target.value)),
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TransDropdownFormInput
                    name="outcome"
                    control={control}
                    options={outcomes}
                    outcome={true}
                    defaultId={defaultValue?.outcome}
                    label="Outcome"
                    register={register('outcome', {
                      onChange: (e) => dispatch(setOutcome(e.target.value)),
                    })}
                  />
                  <TransDropdownFormInput
                    name="contactedByConfirmationCallmadeby"
                    control={control}
                    options={contacts}
                    contact={true}
                    defaultId={defaultValue?.contactedByConfirmationCallmadeby}
                    label="Contacted By (Confirmation Call made by)"
                    register={register('contactedByConfirmationCallmadeby', {
                      onChange: (e) =>
                        dispatch(
                          setContactedByConfirmationCallmadeby(e.target.value)
                        ),
                    })}
                  />
                  <FormInputText
                    name="callTime"
                    type="time"
                    control={control}
                    shrink={true}
                    // errors={!!errors.organizationName}
                    // helperText={errors?.organizationName?.message}
                    label="Call Time"
                    register={register('callTime', {
                      onChange: (e) => dispatch(setCallTime(e.target.value)),
                    })}
                  />
                </Grid>
              </Grid>
              {getSaveCancel(handleClose, null, handleSubmit(onSubmit))}
            </Grid>
          </>
        )}
      </Dialog>
      <ErrorPopUp
        message={errorMessage}
        resetMessage={setErrorMessage}
        openError={openErrorPopUp}
        setOpenError={setOpenErrorPopUP}
      />
      <TransportPdf open={openTransportPdf} setOpen={setOpenTransportPdf} />
      {getFormSuccess(formSuccess, setFormSuccess, isNewTransport)}
    </>
  );
};

export default TransportDialog;
